// ServicesStack.js
import * as React from "react";
import { Box, Paper, Typography, Grid, Stack, Divider, IconButton } from "@mui/material";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import Service1 from '../../Assets/ServicesImages/Water Extraction & Structural Drying.png';  
import Service2 from '../../Assets/ServicesImages/Emergency Roof Tarping & Leak Response.png';
import Service3 from '../../Assets/ServicesImages/Mold Prevention & Remediation.png';
import Service4 from '../../Assets/ServicesImages/Sewage Cleanup.png';  
import Service5 from '../../Assets/ServicesImages/Content Cleaning & Pack Out.png';  
import Service6 from '../../Assets/ServicesImages/Drywall & Flooring Repair.png';  
import Service7 from '../../Assets/ServicesImages/Drywall & Flooring Repair.png';  


const services = [
  {
    number: "01",
    title: "Water Extraction & Structural Drying",
    description:
      "Minutes matter. We remove standing water fast and scientifically dry your home to prevent mold and permanent structural damage.",
    bullets: [
      "24/7 emergency water removal",
      "Industrial-grade extraction & LGR dehumidifiers",
      "Thermal imaging to find hidden moisture",
    ],
    price: "24/7",
    unit: "Emergency Service",
    href: "/services/water-extraction",
    image:
      Service1,
    bg: "#3F8F49",
  },

  {
    number: "02",
    title: "Mold Prevention & Remediation",
    description:
      "Certified mold inspection and remediation to protect your health and eliminate mold at the source—not just on the surface.",
    bullets: [
      "IICRC S520 compliant remediation",
      "Safe containment & HEPA air filtration",
      "Black mold & basement mold specialists",
    ],
    price: "Certified",
    unit: "Mold Experts",
    href: "/services/mold-remediation",
    image:
      Service3,
    bg: "#3F8F49",
  },

  {
    number: "03",
    title: "Sewage Cleanup (Black Water)",
    description:
      "Hazardous sewage and black water cleanup performed safely using strict biohazard protocols and full PPE.",
    bullets: [
      "Category 3 black water handling",
      "Full sanitation & deodorization",
      "Safe disposal of contaminated materials",
    ],
    price: "24/7",
    unit: "Biohazard Cleanup",
    href: "/services/sewage-cleanup",
    image:
      Service4,
    bg: "#3F8F49",
  },

  {
    number: "04",
    title: "Content Cleaning & Pack Out",
    description:
      "Professional pack-out, content cleaning, and climate-controlled storage to protect what matters most to you.",
    bullets: [
      "Secure pack-out & digital inventory",
      "Furniture, electronics & document restoration",
      "Insurance-friendly documentation",
    ],
    price: "Secure",
    unit: "Pack-Out Service",
    href: "/services/content-cleaning",
    image:
      Service5,
   bg: "#3F8F49",
  },

  {
    number: "05",
    title: "Drywall & Flooring Repair",
    description:
      "From flood cuts to final paint, we restore drywall and flooring to pre-loss—or better—condition.",
    bullets: [
      "Seamless drywall patching & painting",
      "Hardwood, carpet, tile & vinyl flooring",
      "Insurance-approved reconstruction",
    ],
    price: "Full",
    unit: "Reconstruction",
    href: "/services/repairs",
    image:
      Service7,
   bg: "#3F8F49",
  },

  {
    number: "06",
    title: "Emergency Roof Tarping & Leak Response",
    description:
      "Immediate roof tarping and leak stops to prevent further water intrusion after storms or tree damage.",
    bullets: [
      "Heavy-duty secured tarp installation",
      "Storm & hail damage response",
      "Interior protection & drying included",
    ],
    price: "24/7",
    unit: "Storm Response",
    href: "/services/emergency-roofing",
    image:
      Service6,
   bg: "#3F8F49",
  },
];


function ServiceCard({ service, index, total }) {
  // Tune these to match your design
  const baseTop = 24;         // distance from viewport top for the first card
  const stackGap = 18;        // how much each next card sits lower when sticky
  const top = baseTop + index * stackGap;

  // This controls how long each card occupies scroll space.
  // Bigger = slower / more dramatic overlap.
  const scrollSpace = 420;

  return (
    <Box
      sx={{
        position: "sticky",
        top,
        zIndex: 10 + index, // later cards sit on top while overlapping
        // This creates the "space" so you can scroll to the next overlap
        mb: index === total - 1 ? 0 : `${scrollSpace}px`,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 6,
          overflow: "hidden",
          color: "#fff",
          background: service.bg,
          position: "relative",
          boxShadow: "0 18px 50px rgba(0,0,0,0.18)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: { xs: 2.5, md: 4 },
            pt: { xs: 2.5, md: 3 },
            pb: { xs: 2, md: 2.5 },
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              letterSpacing: -0.5,
              fontSize: { xs: 28, sm: 34, md: 44 },
              lineHeight: 1.05,
            }}
          >
            {service.title}
          </Typography>

          <Typography
            sx={{
              fontWeight: 800,
              opacity: 1,
              fontSize: { xs: 28, sm: 34, md: 44 },
              lineHeight: 1,
            }}
          >
            {service.number}
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ px: { xs: 2.5, md: 4 }, pb: { xs: 2.5, md: 3.5 } }}>
          <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack spacing={1.5}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, md: 20 } }}>
                  What’s Included?
                </Typography>

                <Typography
                  sx={{
                    opacity: 1,
                    fontSize: { xs: 14.5, md: 15.5 },
                    lineHeight: 1.7,
                  }}
                >
                  {service.description}
                </Typography>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.25)" }} />

                <Stack spacing={1}>
                  {service.bullets.map((b) => (
                    <Stack key={b} direction="row" spacing={1} alignItems="center">
                      <CheckCircleIcon fontSize="small" />
                      <Typography sx={{ fontSize: { xs: 14.5, md: 15.5 } }}>{b}</Typography>
                    </Stack>
                  ))}
                </Stack>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.25)", mt: 1 }} />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    pt: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.2 }}>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        fontSize: { xs: 34, md: 42 },
                        letterSpacing: -0.5,
                      }}
                    >
                      {service.price}
                    </Typography>
                    <Typography sx={{ opacity: 1, fontWeight: 600 }}>
                      {service.unit}
                    </Typography>
                  </Box>

                  <IconButton
                    component="a"
                    // href={service.href}
                    aria-label={`Open ${service.title}`}
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      bgcolor: "rgba(255,165,0,0.95)",
                      color: "#1b1b1b",
                      "&:hover": { bgcolor: "rgba(255,165,0,1)" },
                      boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
                    }}
                  >
                    <NorthEastIcon />
                  </IconButton>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: "100%",
                  minHeight: { xs: 220, sm: 280, md: 320 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={service.image}
                  alt={service.title}
                  loading="lazy"
                  sx={{
                    width: "100%",
                    maxWidth: 520,
                    height: "auto",
                    objectFit: "contain",
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}

export default function ServicesStack() {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1200,
        mx: "auto",
        px: { xs: 2, md: 3 },
        py: { xs: 3, md: 6 },

        // Important for sticky behavior (avoid parents with overflow hidden/auto)
        overflow: "visible",
      }}
    >
      {services.map((s, i) => (
        <ServiceCard key={s.number} service={s} index={i} total={services.length} />
      ))}

      {/* After the stack ends, normal page scroll continues */}
      <Box sx={{ height: 300 }} />
    </Box>
  );
}
